/*
 * bb-gift.js — per-item gift wrapping for Beit Ballout.
 *
 * Product page: the snippet renders real form inputs, so add-to-cart carries
 * the properties natively. This file only handles show/hide, the character
 * counter, and disabling gifting when a subscription (Pantry) is selected.
 *
 * Cart: there are no form inputs, so every change is written through the Cart
 * AJAX API here. Three things this has to get right:
 *
 *   1. /cart/change.js REPLACES the properties object rather than merging it,
 *      so we always read the line's existing properties and send them back
 *      whole. data-bb-props carries them from Liquid.
 *   2. Shopify's cart endpoints handle concurrent writes badly, so every
 *      mutation goes through a single promise queue.
 *   3. The theme re-renders the cart by replacing #AjaxCartForm's innerHTML,
 *      which destroys our listeners. We re-bind on the theme's 'cart-updated'
 *      event (dispatched in component-cart.js).
 *
 * The S$ charge lives on settings.gift_wrap_product. We keep that line's
 * quantity equal to the total number of gift-wrapped units.
 */
(function () {
  'use strict';

  var CFG = window.BBGift || {};
  var WRAP_PRODUCT_ID = parseInt(CFG.wrapProductId, 10) || 0;
  var WRAP_VARIANT_ID = parseInt(CFG.wrapVariantId, 10) || 0;

  if (!WRAP_VARIANT_ID) return;

  var GIFT_PROP = '_bb_gift';
  var MSG_PROP = 'Gift Message';

  /* Visible flag, so a wrapped item with no card message is still obvious on
     the order and in the admin. */
  var FLAG_PROP = 'Gift wrapping';

  /* Shopify merges cart lines whose variant AND properties match exactly. Two
     gifted units must stay as two lines to hold two different messages, so each
     gifted line carries a unique hidden id to keep them apart. */
  var GIFT_ID_PROP = '_bb_gift_id';

  var OUR_PROPS = [GIFT_PROP, MSG_PROP, FLAG_PROP, GIFT_ID_PROP];

  function uid() {
    return 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  var routes = (window.KROWN && window.KROWN.settings && window.KROWN.settings.routes) || {};
  var CHANGE_URL = routes.cart_change_url || '/cart/change';
  var ADD_URL = routes.cart_add_url || '/cart/add';
  var CART_URL = routes.cart_url || '/cart';

  /* Set while we are driving a re-render, so our own 'cart-updated' does not
     re-enter the sync logic and loop. */
  var selfUpdating = false;

  /* ------------------------------------------------------------------ utils */

  var queue = Promise.resolve();
  function enqueue(fn) {
    queue = queue.then(fn).catch(function (e) {
      console.error('[bb-gift]', e);
    });
    return queue;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function postJSON(url, body) {
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function getCart() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function safeParse(str) {
    try { return JSON.parse(str) || {}; } catch (e) { return {}; }
  }

  /* Shopify returns [] rather than {} for a line with no properties. */
  function propsObject(raw) {
    if (!raw || Array.isArray(raw)) return {};
    return raw;
  }

  /* -------------------------------------------------------------- cart sync */

  /* One card per wrapped item. A line item property belongs to the LINE, not to
     each unit, so two units sharing a line can only ever carry one message.
     Split any gifted line with quantity > 1 into single-quantity lines, each
     given a unique _bb_gift_id so Shopify does not merge them straight back
     together. The message is copied to each; the customer then edits them
     independently in the cart.

     Returns true if the cart was changed. */
  function enforceGiftSplit() {
    return getCart().then(function (cart) {
      var targets = cart.items.filter(function (it) {
        var p = propsObject(it.properties);
        return p[GIFT_PROP] === 'true' && it.quantity > 1;
      });

      if (!targets.length) return false;

      var chain = Promise.resolve();

      targets.forEach(function (item) {
        var base = propsObject(item.properties);
        var extra = item.quantity - 1;

        /* Shrink the original to a single unit, giving it an id if it has none. */
        var keep = {};
        Object.keys(base).forEach(function (k) { keep[k] = base[k]; });
        keep[GIFT_ID_PROP] = base[GIFT_ID_PROP] || uid();

        chain = chain.then(function () {
          return postJSON(CHANGE_URL, { id: item.key, quantity: 1, properties: keep });
        });

        /* Re-add the remaining units as their own lines. */
        for (var i = 0; i < extra; i++) {
          chain = chain.then(function () {
            var np = {};
            Object.keys(base).forEach(function (k) { np[k] = base[k]; });
            np[GIFT_ID_PROP] = uid();
            return postJSON(ADD_URL, {
              items: [{ id: item.variant_id, quantity: 1, properties: np }]
            });
          });
        }
      });

      return chain.then(function () { return true; });
    });
  }

  /* Split first, then true up the wrap quantity against the result. */
  function reconcileCart() {
    return enforceGiftSplit().then(function (splitChanged) {
      return syncWrapQuantity().then(function (wrapChanged) {
        return splitChanged || wrapChanged;
      });
    });
  }

  /* Keep the gift wrap line's quantity equal to the number of wrapped units.
     Returns true if the cart was changed. */
  function syncWrapQuantity() {
    return getCart().then(function (cart) {
      var gifted = 0;
      var wrapKey = null;
      var wrapQty = 0;

      cart.items.forEach(function (it) {
        var props = propsObject(it.properties);
        if (props[GIFT_PROP]) gifted += it.quantity;
        if (it.product_id === WRAP_PRODUCT_ID) {
          wrapKey = it.key;
          wrapQty = it.quantity;
        }
      });

      if (gifted > 0) {
        if (!wrapKey) {
          return postJSON(ADD_URL, { items: [{ id: WRAP_VARIANT_ID, quantity: gifted }] })
            .then(function () { return true; });
        }
        if (wrapQty !== gifted) {
          return postJSON(CHANGE_URL, { id: wrapKey, quantity: gifted })
            .then(function () { return true; });
        }
        return false;
      }

      if (wrapKey) {
        return postJSON(CHANGE_URL, { id: wrapKey, quantity: 0 })
          .then(function () { return true; });
      }
      return false;
    });
  }

  /* The /cart page is not the theme's cart at all — templates/cart.json renders
     the custom bb-cart section, which owns its own markup and refresh. Re-render
     it the same way it re-renders itself. */
  function rerenderBBCart() {
    var host = document.querySelector('[data-bb-cart-contents]');
    if (!host) return Promise.resolve();

    return fetch(window.location.pathname + '?section_id=bb-cart')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-bb-cart-contents]');
        if (fresh) host.innerHTML = fresh.innerHTML;
        bindCartControls();
        /* bb-cart owns the self-pickup date/time picker and builds it in JS.
           Replacing the section's HTML wipes it, leaving bare day headings, so
           ask bb-cart to rebuild it. Deliberately a different event from
           'bb-cart:refreshed', which we listen to ourselves. */
        document.dispatchEvent(new Event('bb-cart:reinit'));
      });
  }

  /* Mirrors the re-render in component-cart.js. helper-cart renders the cart
     drawer, which exists on every page including /cart. */
  function rerenderCart() {
    return rerenderBBCart().then(function () {
    return fetch(window.location.pathname + '?section_id=helper-cart')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.getElementById('AjaxCartForm');
        var current = document.getElementById('AjaxCartForm');

        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          if (typeof current.ajaxifyCartItems === 'function') current.ajaxifyCartItems();

          var count = current.querySelector('[data-cart-count]');
          var total = current.querySelector('[data-cart-total]');
          if (count) {
            document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
              el.textContent = count.textContent;
            });
          }
          if (total) {
            document.querySelectorAll('[data-header-cart-total]').forEach(function (el) {
              el.textContent = total.textContent;
            });
          }

          selfUpdating = true;
          current.dispatchEvent(new Event('cart-updated'));
          selfUpdating = false;
        }

        var freshSub = doc.getElementById('AjaxCartSubtotal');
        var currentSub = document.getElementById('AjaxCartSubtotal');
        if (freshSub && currentSub) currentSub.innerHTML = freshSub.innerHTML;

        bindCartControls();
      });
    });
  }

  /* Write the gift state for one cart line. */
  function writeLine(block, checked, message) {
    var lineEl = block.closest('[data-js-cart-item]');
    if (!lineEl) return Promise.resolve();

    var key = lineEl.getAttribute('data-id');
    if (!key) return Promise.resolve();

    /* Quantity MUST be sent explicitly. /cart/change.js resets the line to 1
       when quantity is omitted, which silently destroyed customer quantities. */
    var qty = parseInt(lineEl.getAttribute('data-qty'), 10);
    if (!qty || qty < 1) qty = 1;

    /* Rebuild the full properties object: everything that was there, minus
       ours, plus ours. Removing a property means sending it as an empty
       string — an empty properties object is ignored, not treated as a clear. */
    var props = {};
    var existing = propsObject(safeParse(block.getAttribute('data-bb-props')));
    Object.keys(existing).forEach(function (k) {
      if (OUR_PROPS.indexOf(k) === -1) props[k] = existing[k];
    });

    if (checked) {
      props[GIFT_PROP] = 'true';
      props[FLAG_PROP] = 'Yes';
      props[MSG_PROP] = (message || '').trim();
      props[GIFT_ID_PROP] = existing[GIFT_ID_PROP] || uid();
    } else {
      props[GIFT_PROP] = '';
      props[FLAG_PROP] = '';
      props[MSG_PROP] = '';
      props[GIFT_ID_PROP] = '';
    }

    return postJSON(CHANGE_URL, { id: key, quantity: qty, properties: props })
      .then(reconcileCart)
      .then(rerenderCart);
  }

  /* ------------------------------------------------------------- ui binding */

  function updateCount(block) {
    var msg = block.querySelector('[data-bb-gift-msg]');
    var out = block.querySelector('[data-bb-gift-count]');
    if (msg && out) out.textContent = msg.value.length;
  }

  function setPanel(block, open) {
    var panel = block.querySelector('[data-bb-gift-panel]');
    var msg = block.querySelector('[data-bb-gift-msg]');
    var flag = block.querySelector('[data-bb-gift-flag]');
    block.classList.toggle('is-on', open);
    if (panel) panel.hidden = !open;
    /* Disabled inputs are not submitted, which keeps an empty "Gift Message"
       property off the line when gifting is unticked on the product page. */
    if (msg) msg.disabled = !open;
    if (flag) flag.disabled = !open;
  }

  function busy(block, state) {
    var flag = block.querySelector('[data-bb-gift-saving]');
    block.classList.toggle('is-busy', !!state);
    if (flag) flag.hidden = !state;
  }

  function bindProductControls() {
    document.querySelectorAll('[data-bb-gift-context="product"]:not([data-bb-bound])').forEach(function (block) {
      block.setAttribute('data-bb-bound', '1');

      var check = block.querySelector('[data-bb-gift-check]');
      var msg = block.querySelector('[data-bb-gift-msg]');

      if (check) {
        check.addEventListener('change', function () {
          setPanel(block, check.checked);
          if (check.checked && msg) msg.focus();
        });
      }
      if (msg) msg.addEventListener('input', function () { updateCount(block); });

      /* Pantry (subscription) selected → gifting off. A property on a
         subscription line would repeat on every future delivery unpaid. */
      var radios = document.querySelectorAll('[data-bb-purchase]');
      if (radios.length) {
        radios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            var isSub = radio.checked && radio.value === 'pantry';
            block.hidden = isSub;
            if (isSub && check && check.checked) {
              check.checked = false;
              setPanel(block, false);
            }
          });
        });
      }
    });
  }

  function bindCartControls() {
    document.querySelectorAll('[data-bb-gift-context="cart"]:not([data-bb-bound])').forEach(function (block) {
      block.setAttribute('data-bb-bound', '1');

      var check = block.querySelector('[data-bb-gift-check]');
      var msg = block.querySelector('[data-bb-gift-msg]');

      if (check) {
        check.addEventListener('change', function () {
          var on = check.checked;
          setPanel(block, on);
          busy(block, true);
          enqueue(function () {
            return writeLine(block, on, msg ? msg.value : '');
          });
        });
      }

      if (msg) {
        msg.addEventListener('input', function () { updateCount(block); });
        msg.addEventListener('input', debounce(function () {
          if (!check || !check.checked) return;
          busy(block, true);
          enqueue(function () {
            return writeLine(block, true, msg.value);
          });
        }, 700));
      }
    });
  }

  /* Adding from the product page never touches the cart form, so nothing above
     would run and the wrap charge would be missed. <product-form> dispatches a
     non-bubbling 'add-to-cart' on itself (component-product-form.js), so bind
     each form directly rather than listening on document. */
  function bindProductFormSync() {
    document.querySelectorAll('product-form:not([data-bb-gift-watched])').forEach(function (pf) {
      pf.setAttribute('data-bb-gift-watched', '1');
      pf.addEventListener('add-to-cart', function () {
        enqueue(function () {
          return reconcileCart().then(function (changed) {
            if (changed) return rerenderCart();
            bindCartControls();
          });
        });
      });
    });
  }

  /* The theme fires 'cart-updated' after its own quantity/remove operations.
     Those can leave the wrap quantity stale — e.g. removing a gifted line, or
     bumping a gifted line from 1 to 3 — so re-check and correct. */
  function watchCart() {
    var cartForm = document.getElementById('AjaxCartForm');
    if (!cartForm || cartForm.getAttribute('data-bb-watched')) return;
    cartForm.setAttribute('data-bb-watched', '1');

    cartForm.addEventListener('cart-updated', function () {
      bindCartControls();
      if (selfUpdating) return;
      enqueue(function () {
        return reconcileCart().then(function (changed) {
          if (changed) return rerenderCart();
        });
      });
    });
  }

  /* bb-cart fires this after its own quantity/remove refresh. Same job as the
     'cart-updated' listener above, for the custom cart page. */
  function watchBBCart() {
    document.addEventListener('bb-cart:refreshed', function () {
      bindCartControls();
      if (selfUpdating) return;
      enqueue(function () {
        return reconcileCart().then(function (changed) {
          if (changed) return rerenderCart();
        });
      });
    });
  }

  function init() {
    bindProductControls();
    bindCartControls();
    bindProductFormSync();
    watchCart();
  }

  /* Registered once — init() re-runs on section load and would otherwise stack
     duplicate document listeners. */
  watchBBCart();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The drawer can be injected after load on some templates. */
  document.addEventListener('shopify:section:load', init);
})();
